'use strict';

const Gate = require('./gate');
const QubitPool = require('./qubitPool');

const DECOMPOSABLE_GATES = ['xx', 'yy', 'zz'];

/**
 * This class represents the series of operations necessary to
 * execute a quantum circuit.  Qubit instances reference a target
 * circuit making their gate calling convention more natural.
 */
class Circuit {
    static serializePauliExp(op) {
        if (op['coefficients'].some((coeff) => Number.isNaN(coeff))) {
            throw new Error(
                `pauli exponential coefficients must all be real numbers: ${op}`,
            );
        }
        const coeffString = op['coefficients']
            .map((coeff) => coeff.toString())
            .join(',');
        const termsString = op['terms'].join(',');
        const pauliExpString = `op exp [${op['targets']}] ${op['time']} ${coeffString} ${termsString}\n`;
        return pauliExpString;
    }

    static fromJSON(json) {
        const c = new Circuit();

        if (!json) {
            return c; // return empty circuit in the base case
        }

        const desc = typeof json === 'object' ? json : JSON.parse(json);

        if (desc.qubits) {
            c.alloc(desc.qubits);
        }

        if (desc.circuit) {
            // supporting nested op groups
            desc.circuit
                .map((o) => (o instanceof Array ? o : [o]))
                .forEach((ops) => {
                    ops.forEach((op) => {
                        if (op.gate) {
                            let {
                                gate,
                                target,
                                targets,
                                control,
                                controls,
                                rotation,
                            } = op;
                            if (gate === 'pauliexp') {
                                c._addPauliExp(op);
                                return;
                            }
                            if (!c.qubits || !c.qubits[0]) {
                                throw new Error(`no qubits allocated`);
                            }
                            if (!c.qubits[0][gate.toLowerCase()]) {
                                throw new Error(`illegal gate: "${gate}"`);
                            }

                            if (typeof targets === 'number')
                                targets = [targets];
                            if (typeof controls === 'number')
                                controls = [controls];

                            // FIXME: CNOT should probably be a first class type?
                            if (gate.toUpperCase() === 'CNOT') {
                                gate = 'NOT';
                                [targets, controls] = [controls, targets];
                            }

                            c._addGate(
                                gate.toUpperCase(),
                                target !== undefined
                                    ? [c.qubits[target]]
                                    : targets &&
                                          targets.map(
                                              (qubitIndex) =>
                                                  c.qubits[qubitIndex],
                                          ),
                                control !== undefined
                                    ? [c.qubits[control]]
                                    : controls &&
                                          controls.map(
                                              (qubitIndex) =>
                                                  c.qubits[qubitIndex],
                                          ),
                                rotation,
                            );
                        }
                    });
                });
        }

        return c;
    }

    constructor(pool, ops, options) {
        this.pool = pool || new QubitPool();
        this.ops = ops || []; // time ordered ops
        this.options = options || {};
        this.sets = {}; // named qubit sets
        this.params = {}; // named parameters
        this.shotCount = 1000; // default number of shots for execution
        this.qubits = [];
    }

    generateStats() {
        this.qubitDepths = new Array(this.maxQubits).fill(0);
        this.gateCounts = { 1: 0, 2: 0 };
        this.ops.forEach((op) => {
            if (op.gate) {
                const gate = op.gate;
                const maxOldDepth = Math.max(
                    ...[...gate.targets, ...gate.controls].map(
                        (qubitIndex) => this.qubitDepths[qubitIndex],
                    ),
                );
                for (const qubitIndex of [...gate.targets, ...gate.controls]) {
                    this.qubitDepths[qubitIndex] = maxOldDepth + 1;
                }
                const nQubits =
                    (gate.targets || []).length + (gate.controls || []).length;
                this.gateCounts[nQubits] = (this.gateCounts[nQubits] || 0) + 1;
            } else if (op.pauliexp) {
                const pauliexp = op.pauliexp;
                const terms = pauliexp.terms;
                const targets = pauliexp.targets;
                terms.forEach((term) => {
                    let n_non_zi_pauli_ops = 0;
                    let n_non_identity_pauli_ops = 0;
                    const involvedQubits = [];
                    for (let i = 0; i < term.length; i++) {
                        const p = term[i];
                        if (p !== 'I' && p !== 'Z') {
                            n_non_zi_pauli_ops += 1;
                        }
                        if (p !== 'I') {
                            n_non_identity_pauli_ops += 1;
                        }
                        if (p !== 'I') {
                            involvedQubits.push(targets[i]);
                        }
                    }
                    this.gateCounts[1] += 2 * n_non_zi_pauli_ops + 1;
                    this.gateCounts[2] +=
                        2 * Math.max(n_non_identity_pauli_ops - 1, 0);
                    // Update qubit depths
                    const maxOldDepth = Math.max(
                        ...involvedQubits.map(
                            (qubitIndex) => this.qubitDepths[qubitIndex],
                        ),
                    );
                    for (const qubitIndex of involvedQubits) {
                        this.qubitDepths[qubitIndex] = maxOldDepth + 1;
                    }
                });
            }
        });
    }

    stream(callback) {
        this.options.streamOp = callback;
    }

    clone() {
        let newCircuit = new Circuit(undefined, undefined, this.options);
        let qubits = [];
        for (let i = 0; i < this.pool.maxQubits; ++i) {
            qubits.push(newCircuit.alloc());
        }
        this.ops.forEach((op) => {
            if (op.alloc) {
                // all qubits were already allocated
            } else if (op.gate) {
                const gate = op.gate.type.toLowerCase();
                const targets = op.gate.targets.map((qi) => qubits[qi]);
                const controls = op.gate.controls.map((qi, i) =>
                    qubits[qi].asControl(!op.gate.controlsOC[i]),
                );
                const rotation = op.gate.rotation;
                switch (gate) {
                    case 'x':
                    case 'y':
                    case 'z':
                        targets.forEach((target) => {
                            target[gate](controls, rotation);
                        });
                        break;
                    case 'rx':
                    case 'ry':
                    case 'rz':
                        targets.forEach((target) => {
                            target[gate](rotation, controls);
                        });
                        break;
                    case 'h':
                    case 's':
                    case 'si':
                    case 'v':
                    case 'vi':
                    case 't':
                    case 'ti':
                    case 'not':
                        targets.forEach((target) => {
                            target[gate](controls);
                        });
                        break;
                    case 'swap':
                        targets[0][gate](targets[1], controls);
                        break;
                    case 'xx':
                    case 'yy':
                    case 'zz':
                        targets[0][gate](targets[1], controls, rotation);
                        break;
                    default:
                        throw new Error(`unknown gate ${gate}`);
                }
            } else if (op.sample) {
                newCircuit.sample(op.sample);
            } else if (op.label) {
                newCircuit.label(op.label);
            } else {
                throw new Error(`unknown op: ${op}`);
            }
        });
        return newCircuit;
    }

    get maxQubits() {
        return this.pool.maxQubits;
    }

    // named qubit input array
    /**
     * @param {String} name of qubit array
     * @param {Array || Number} qubits array or count
     */
    input(name, qubits) {
        if (Array.isArray(qubits)) {
            // set named input array
            this.sets[name] = qubits;
            return qubits;
        } else if (typeof qubits === 'number') {
            let array = this.sets[name];
            if (array) {
                // if the array exists, verify length
                if (array.length !== qubits) {
                    throw new Error(`input mismatch on ${name}`);
                }
            } else {
                // otherwise, input is missing, so allocate qubits
                array = this.sets[name] = this.allocateQubits(qubits);
            }
            return array;
        } else {
            throw new Error('invalid qubits type');
        }
    }

    shots(number) {
        this.shotCount = number;
        return this;
    }

    // named input parameter defaults to existing value
    // since they are seeded before the codec parses the body
    // and only defaults fall throught when not sent in call()
    param(name, value) {
        if (typeof this.params[name] !== 'number') {
            this.params[name] = this.params[name] || value;
            if (typeof this.params[name] === 'undefined') {
                this.params[name] = null;
            }
        }
        return this;
    }

    alloc(n) {
        n = Math.max(n || 1, 1);
        return n > 1 ? this.allocateQubits(n) : this.allocateQubit();
    }

    allocateQubit() {
        this._addOp({
            alloc: 1,
        });
        return this._alloc();
    }

    allocateQubits(count) {
        this._addOp({
            alloc: count,
        });
        const qubits = [];
        while (count > 0) {
            qubits.push(this._alloc());
            count--;
        }
        return qubits;
    }

    /**
     * @param {String} uuid of target script
     * @param {Qubit[]} qubits inputs to script
     * @param {Object} params key value strings pairs
     */
    call(uuid, qubits, params) {
        this._addOp({
            call: uuid,
            qubits: qubits,
            params: params,
        });
        return this;
    }

    label(label) {
        this._addOp({
            label: label || 'end',
        });
        return this;
    }

    sample(value) {
        this._addOp({
            sample: value,
        });
        return this;
    }

    static decomposeGate(op, out, debugFlag) {
        let count = 0;
        const debug = debugFlag
            ? () => {
                  out.push(Circuit.serializeOp({ sample: 'debug' }));
                  count++;
              }
            : () => {};
        const type = op.gate.type.toLowerCase();
        if (['xx', 'yy', 'zz'].includes(type)) {
            // local compensating change of basis intro
            if (type === 'yy') {
                out.push(
                    Circuit.serializeOp({
                        gate: {
                            type: 'si',
                            targets: [op.gate.targets[0]],
                            controls: op.gate.controls,
                            controlsOC: op.gate.controlsOC,
                        },
                    }),
                );
                ++count;
                debug();
                out.push(
                    Circuit.serializeOp({
                        gate: {
                            type: 'si',
                            targets: [op.gate.targets[1]],
                            controls: op.gate.controls,
                            controlsOC: op.gate.controlsOC,
                        },
                    }),
                );
                ++count;
                debug();
            }
            if (type === 'xx' || type === 'yy') {
                out.push(
                    Circuit.serializeOp({
                        gate: {
                            type: 'h',
                            targets: [op.gate.targets[0]],
                            controls: op.gate.controls,
                            controlsOC: op.gate.controlsOC,
                        },
                    }),
                );
                ++count;
                debug();
                out.push(
                    Circuit.serializeOp({
                        gate: {
                            type: 'h',
                            targets: [op.gate.targets[1]],
                            controls: op.gate.controls,
                            controlsOC: op.gate.controlsOC,
                        },
                    }),
                );
                ++count;
                debug();
            }
            // rz on "target" of follow up crz
            out.push(
                Circuit.serializeOp({
                    gate: {
                        type: 'rz',
                        targets: [op.gate.targets[1]],
                        controls: op.gate.controls,
                        controlsOC: op.gate.controlsOC,
                        rotation: op.gate.rotation,
                    },
                }),
            );
            ++count;
            // rz(theta, q2) + crz(-2 * theta, q1, q2) === ZZ(theta, q1, q2)
            out.push(
                Circuit.serializeOp({
                    gate: {
                        type: 'rz',
                        targets: [op.gate.targets[1]],
                        controls: [...op.gate.controls, op.gate.targets[0]],
                        controlsOC: [...op.gate.controlsOC, true],
                        rotation: -2 * op.gate.rotation,
                    },
                }),
            );
            ++count;
            debug();
            // local compensating change of basis outro
            if (type === 'xx' || type === 'yy') {
                out.push(
                    Circuit.serializeOp({
                        gate: {
                            type: 'h',
                            targets: [op.gate.targets[0]],
                            controls: op.gate.controls,
                            controlsOC: op.gate.controlsOC,
                        },
                    }),
                );
                ++count;
                debug();
                out.push(
                    Circuit.serializeOp({
                        gate: {
                            type: 'h',
                            targets: [op.gate.targets[1]],
                            controls: op.gate.controls,
                            controlsOC: op.gate.controlsOC,
                        },
                    }),
                );
                ++count;
                debug();
            }
            if (type === 'yy') {
                out.push(
                    Circuit.serializeOp({
                        gate: {
                            type: 's',
                            targets: [op.gate.targets[0]],
                            controls: op.gate.controls,
                            controlsOC: op.gate.controlsOC,
                        },
                    }),
                );
                ++count;
                debug();
                out.push(
                    Circuit.serializeOp({
                        gate: {
                            type: 's',
                            targets: [op.gate.targets[1]],
                            controls: op.gate.controls,
                            controlsOC: op.gate.controlsOC,
                        },
                    }),
                );
                ++count;
                debug();
            }
        } else {
            if (DECOMPOSABLE_GATES.includes(type)) {
                throw new Error(
                    `decomposition for ${type} gate unimplemented!`,
                );
            }
            throw new Error(`gate ${type} cannot be decomposed!`);
        }
        return count;
    }

    decompose() {
        const newOps = [];
        for (const op of this.ops) {
            if (
                op.gate &&
                DECOMPOSABLE_GATES.includes(op.gate.type.toLowerCase())
            ) {
                const type = op.gate.type.toLowerCase();
                if (['xx', 'yy', 'zz'].includes(type)) {
                    if (type === 'yy') {
                        newOps.push(
                            {
                                gate: {
                                    type: 'si',
                                    targets: [op.gate.targets[0]],
                                    controls: op.gate.controls,
                                    controlsOC: op.gate.controlsOC,
                                },
                            },
                            {
                                gate: {
                                    type: 'si',
                                    targets: [op.gate.targets[1]],
                                    controls: op.gate.controls,
                                    controlsOC: op.gate.controlsOC,
                                },
                            },
                        );
                    }
                    if (type === 'xx' || type === 'yy') {
                        newOps.push(
                            {
                                gate: {
                                    type: 'h',
                                    targets: [op.gate.targets[0]],
                                    controls: op.gate.controls,
                                    controlsOC: op.gate.controlsOC,
                                },
                            },
                            {
                                gate: {
                                    type: 'h',
                                    targets: [op.gate.targets[0]],
                                    controls: op.gate.controls,
                                    controlsOC: op.gate.controlsOC,
                                },
                            },
                        );
                    }
                    newOps.push(
                        {
                            gate: {
                                type: 'z',
                                targets: [op.gate.targets[1]],
                                controls: [
                                    ...op.gate.controls,
                                    op.gate.targets[0],
                                ],
                                controlsOC: [...op.gate.controlsOC, true],
                                rotation: -2 * op.gate.rotation,
                            },
                        },
                        {
                            gate: {
                                type: 'rz',
                                targets: [op.gate.targets[1]],
                                controls: op.gate.controls,
                                controlsOC: op.gate.controlsOC,
                                rotation: op.gate.rotation,
                            },
                        },
                        {
                            gate: {
                                type: 'rz',
                                targets: [op.gate.targets[0]],
                                controls: op.gate.controls,
                                controlsOC: op.gate.controlsOC,
                                rotation: op.gate.rotation,
                            },
                        },
                    );
                    if (type === 'xx' || type === 'yy') {
                        newOps.push(
                            {
                                gate: {
                                    type: 'h',
                                    targets: [op.gate.targets[0]],
                                    controls: op.gate.controls,
                                    controlsOC: op.gate.controlsOC,
                                },
                            },
                            {
                                gate: {
                                    type: 'h',
                                    targets: [op.gate.targets[0]],
                                    controls: op.gate.controls,
                                    controlsOC: op.gate.controlsOC,
                                },
                            },
                        );
                    }
                    if (type === 'yy') {
                        newOps.push(
                            {
                                gate: {
                                    type: 's',
                                    targets: [op.gate.targets[0]],
                                    controls: op.gate.controls,
                                    controlsOC: op.gate.controlsOC,
                                },
                            },
                            {
                                gate: {
                                    type: 's',
                                    targets: [op.gate.targets[1]],
                                    controls: op.gate.controls,
                                    controlsOC: op.gate.controlsOC,
                                },
                            },
                        );
                    }
                } else {
                    throw new Error(
                        `decomposition for ${type} gate unimplemented!`,
                    );
                }
            } else {
                newOps.push(op);
            }
        }
        this.ops = newOps;
    }

    encodeJSON() {
        const out = {
            $id: 'http://ionq.co/schemas/circit-v0.json',
            qubits: this.maxQubits,
            circuit: [],
        };

        this.ops.forEach((op) => {
            if (op.gate) {
                let { type, targets, controls, rotation } = op.gate;

                const gate = {
                    gate: type,
                    targets,
                };

                if (controls) gate.controls = controls;
                if (typeof rotation === 'number') gate.rotation = rotation;

                out.circuit.push(gate);
            }
        });

        return JSON.stringify(out);
    }

    /**
     * @returns {String} string suitable for script.body
     */
    encode(decompose = false) {
        const out = [`alloc [${this.maxQubits}]`];

        let debug = false;
        let count = 2; // alloc + free

        this.ops.forEach((op) => {
            if (op.pauliexp) {
                out.push(Circuit.serializePauliExp(op['pauliexp']));
                count += 1;
            } else if (op.call) {
                out.push(Circuit.serializeOp(op));
            } else if (op.label) {
                out.push(Circuit.serializeOp(op));
                count++;
            } else if (op.sample) {
                if (debug) {
                    return;
                }
                out.push(Circuit.serializeOp(op));
                count++;
            } else if (op.gate) {
                if (
                    decompose &&
                    DECOMPOSABLE_GATES.includes(op.gate.type.toLowerCase())
                ) {
                    count += Circuit.decomposeGate(op, out, debug);
                    return;
                }
                out.push(Circuit.serializeOp(op));
                count++;
                if (debug) {
                    out.push(Circuit.serializeOp({ sample: 'debug' }));
                    count++;
                }
            } else if (typeof op.debug === 'boolean') {
                debug = op.debug;
            }
        });

        out.push('free');
        out.splice(
            0,
            0,
            `// max qubit ${this.maxQubits}`,
            `// ops count ${count}`,
            `// shots ${this.shotCount}`,
        );

        return out.join('\n') + '\n';
    }

    static genControls(gate) {
        if (gate.controls.length === 0) {
            return '';
        }
        let controls = [];
        for (let i = 0; i < gate.controls.length; ++i) {
            controls.push((gate.controlsOC[i] ? '' : '-') + gate.controls[i]);
        }
        return `[${controls.join(',')}]`;
    }

    static serializeOp(op) {
        if (op.call) {
            let key;
            let args = [];
            if (op.qubits) {
                for (key in op.qubits) {
                    args.push(
                        `${key}=[${op.qubits[key]
                            .map((q) => q.index)
                            .join(',')}]`,
                    );
                }
            }
            if (op.params) {
                for (key in op.params) {
                    args.push(`${key}=${op.params[key]}`);
                }
            }
            return `call ${op.call} ${args.join(' ')}`;
        } else if (op.label) {
            return `label ${op.label}`;
        } else if (op.sample) {
            return `sample ${op.sample}`;
        } else if (op.gate) {
            if (Gate.acceptsAngle(op.gate.type)) {
                return `op ${op.gate.type.toLowerCase()} [${op.gate.targets.join(
                    ',',
                )}] ${Circuit.genControls(op.gate)} ${op.gate.rotation}`;
            } else {
                return `op ${op.gate.type.toLowerCase()} [${op.gate.targets.join(
                    ',',
                )}] ${Circuit.genControls(op.gate)}`;
            }
            // eslint-disable-next-line no-empty
        } else if (op.alloc) {
            // eslint-disable-next-line no-empty
        } else if (op.free) {
        } else {
            console.error('unknown op: ', JSON.stringify(op));
        }
    }

    _alloc() {
        return this.pool.alloc(this);
    }

    _free(qubit) {
        if (!qubit.circuit) {
            throw new Error('double free qubit');
        }
        this._addOp({
            free: qubit,
        });
        qubit.circuit = null;
        this.pool.free(qubit);
    }

    _addOp(op) {
        if (typeof this.options.streamOp === 'function') {
            this.options.streamOp(op);
        } else {
            this.ops.push(op);
        }
    }

    _addGate(gateType, targets, controls, rotation) {
        this._addOp({ gate: new Gate(gateType, targets, controls, rotation) });
    }

    _addPauliExp(op) {
        this._addOp({ pauliexp: op });
    }
}

module.exports = Circuit;
